import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TopbarGlobalSearchBody from './TopbarGlobalSearchBody.jsx';
import TopbarGlobalSearchFilters from './TopbarGlobalSearchFilters.jsx';
import TopbarGlobalSearchFooter from './TopbarGlobalSearchFooter.jsx';
import TopbarGlobalSearchHeader from './TopbarGlobalSearchHeader.jsx';
import {
  buildGlobalSearchCategories,
  buildGlobalSearchFilterSections,
  buildGlobalSearchTypeEntities,
  GLOBAL_SEARCH_MODAL_MAX_HEIGHT_CLASS,
  GLOBAL_SEARCH_MODAL_MOBILE_CONTAINER_CLASS,
  GLOBAL_SEARCH_MODAL_MOBILE_PANEL_CLASS,
  GLOBAL_SEARCH_MODAL_PANEL_CLASS,
  GLOBAL_SEARCH_SEPARATOR_CLASS,
  selectSingleFilterInGroup,
} from './constants.js';
import GlobalSearchShortcutListener from './GlobalSearchShortcutListener.jsx';

const EMPTY_ARRAY = [];
const EMPTY_LABELS = {};

/**
 * Global search command palette (Figma Global Search - menu, 700px).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => void} [props.onOpen] - Required when `enableSearchShortcut` is true (opens on Ctrl/Cmd+K)
 * @param {boolean} [props.enableSearchShortcut=false] - Registers Ctrl/Cmd+K and / while this component is mounted
 * @param {string} [props.query]
 * @param {(value: string) => void} [props.onQueryChange]
 * @param {string} [props.placeholder]
 * @param {string[]} [props.selectedFilterIds]
 * @param {(ids: string[]) => void} [props.onFiltersChange]
 * @param {string} [props.activeCategory]
 * @param {(categoryId: string) => void} [props.onCategoryChange]
 * @param {'idle'|'loading'|'results'|'empty'|'error'} [props.status]
 * @param {Array<object>} [props.suggestedActions]
 * @param {Array<object>} [props.recentItems]
 * @param {Array<object>} [props.results] - Flat results (used when groupedResults is empty)
 * @param {Array<{ id: string, label: string, icon?: import('lucide-react').LucideIcon, items: Array<object>, totalCount?: number, onSeeAll?: () => void }>} [props.groupedResults]
 * @param {number} [props.highlightedIndex] - Controlled highlighted index (overrides internal keyboard nav)
 * @param {(item: object) => void} [props.onSelectResult]
 * @param {(item: object) => void} [props.onSelectResultNewTab] - Fired on Cmd/Ctrl+Enter
 * @param {(action: object) => void} [props.onSelectAction]
 * @param {() => void} [props.onClearFilters]
 * @param {() => void} [props.onSearchAll]
 * @param {() => void} [props.onRetry] - Shown in error state
 * @param {Record<string, string>} [props.labels]
 * @param {() => void} [props.onAdvancedSearch]
 * @param {Array<{ id: string, label: string, icon?: import('lucide-react').LucideIcon, count?: number }>} [props.categories]
 * @param {string} [props.className]
 */
const TopbarGlobalSearchModal = ({
  isOpen,
  onClose,
  onOpen,
  enableSearchShortcut = false,
  query = '',
  onQueryChange,
  placeholder = 'Search agents, networks, tickets, policies people...',
  activeFilters = EMPTY_ARRAY,
  onRemoveFilter,
  selectedFilterIds = EMPTY_ARRAY,
  onFiltersChange,
  activeCategory = 'all',
  onCategoryChange,
  status = 'idle',
  suggestedActions = EMPTY_ARRAY,
  recentItems = EMPTY_ARRAY,
  results = EMPTY_ARRAY,
  groupedResults = EMPTY_ARRAY,
  highlightedIndex: highlightedIndexProp = -1,
  onSelectResult,
  onSelectResultNewTab,
  onSelectAction,
  onClearFilters,
  onSearchAll,
  onRetry,
  onAdvancedSearch,
  categories: categoriesProp,
  labels = EMPTY_LABELS,
  useBodyPortal = true,
  className = '',
}) => {
  const titleId = useId();
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const [navIndex, setNavIndex] = useState(-1);

  const typeEntities = useMemo(() => buildGlobalSearchTypeEntities(labels), [labels]);
  const categories = useMemo(
    () => categoriesProp ?? buildGlobalSearchCategories(labels, typeEntities),
    [categoriesProp, labels, typeEntities],
  );
  const filterSections = useMemo(
    () => buildGlobalSearchFilterSections(labels, typeEntities),
    [labels, typeEntities],
  );

  const flatResults = useMemo(() => {
    if (groupedResults.length > 0) return groupedResults.flatMap((g) => g.items);
    return results;
  }, [groupedResults, results]);

  // H3: stable string key — only changes when the actual result set changes, not on every render
  const resultsKey = useMemo(
    () => flatResults.map((r) => r.id).join('|'),
    [flatResults],
  );

  // Volatile values read inside event handlers — updated every render via ref, no dep array churn
  const stateRef = useRef(null);
  stateRef.current = { status, flatResults, navIndex, onClose, onSelectResult, onSelectResultNewTab };

  useEffect(() => { setNavIndex(-1); }, [resultsKey]);
  useEffect(() => { if (!isOpen) setNavIndex(-1); }, [isOpen]);

  const effectiveHighlightIndex = highlightedIndexProp !== -1 ? highlightedIndexProp : navIndex;

  if (import.meta.env?.DEV && enableSearchShortcut && typeof onOpen !== 'function') {
    console.warn(
      '[TopbarGlobalSearchModal] enableSearchShortcut requires an onOpen callback to open via Ctrl/Cmd+K.',
    );
  }

  // H4: focus input on open — only fires when isOpen changes, not on every re-render
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.querySelector?.('input')?.focus?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  // H4: keydown listener — reads volatile values from stateRef so this effect only
  // attaches/detaches when the modal opens or closes, never on mid-session re-renders
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      const { status, flatResults, navIndex, onClose, onSelectResult, onSelectResultNewTab } =
        stateRef.current;

      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (!panelRef.current?.contains(document.activeElement)) return;

      if (status === 'results' && flatResults.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setNavIndex((i) => Math.min(i + 1, flatResults.length - 1));
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setNavIndex((prev) => {
            if (prev <= 0) {
              inputRef.current?.querySelector('input')?.focus();
              return -1;
            }
            return prev - 1;
          });
        } else if (event.key === 'Enter' && navIndex >= 0) {
          event.preventDefault();
          const item = flatResults[navIndex];
          if (item) {
            if (event.metaKey || event.ctrlKey) {
              onSelectResultNewTab?.(item);
            } else {
              onSelectResult?.(item);
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose?.();
      }
    },
    [onClose],
  );

  const handlePanelClick = useCallback((event) => event.stopPropagation(), []);

  const handleAddFilter = useCallback(
    (filterId) => {
      const next = selectSingleFilterInGroup(selectedFilterIds, filterId);
      onFiltersChange?.(next);
    },
    [selectedFilterIds, onFiltersChange],
  );

  const shortcutListener = enableSearchShortcut ? (
    <GlobalSearchShortcutListener isOpen={isOpen} onOpen={onOpen} onClose={onClose} />
  ) : null;

  if (!isOpen) {
    return shortcutListener;
  }

  const modalTree = (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm max-md:backdrop-blur-none"
        onClick={handleBackdropClick}
        role="presentation"
        aria-hidden
      />
      <div
        className={[
          'fixed inset-0 z-[61] flex items-start justify-center p-4 pt-[10vh] pointer-events-none sm:pt-[12vh]',
          GLOBAL_SEARCH_MODAL_MOBILE_CONTAINER_CLASS,
        ].join(' ')}
        role="presentation"
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={[
            GLOBAL_SEARCH_MODAL_PANEL_CLASS,
            GLOBAL_SEARCH_MODAL_MAX_HEIGHT_CLASS,
            GLOBAL_SEARCH_MODAL_MOBILE_PANEL_CLASS,
            'pointer-events-auto flex flex-col',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={handlePanelClick}
        >
          <span id={titleId} className="sr-only">
            {labels.dialogTitle ?? 'Global search'}
          </span>
          <TopbarGlobalSearchHeader
            inputRef={inputRef}
            query={query}
            onQueryChange={onQueryChange}
            placeholder={placeholder}
            activeFilters={activeFilters}
            onRemoveFilter={onRemoveFilter}
            onClose={onClose}
            escLabel={labels.esc ?? 'esc'}
            cancelLabel={labels.cancel ?? 'Cancel'}
            filterSections={filterSections}
            onAddFilter={handleAddFilter}
          />
          <div className={['h-px shrink-0', GLOBAL_SEARCH_SEPARATOR_CLASS].join(' ')} role="separator" />
          <TopbarGlobalSearchFilters
            categories={categories}
            filterSections={filterSections}
            activeCategory={activeCategory}
            onCategoryChange={onCategoryChange}
            selectedFilterIds={selectedFilterIds}
            onFiltersChange={onFiltersChange}
            labels={labels}
            filtersAriaLabel={labels.filtersAriaLabel}
          />
          <div className={['h-px shrink-0', GLOBAL_SEARCH_SEPARATOR_CLASS].join(' ')} role="separator" />
          <TopbarGlobalSearchBody
            status={status}
            suggestedActions={suggestedActions}
            recentItems={recentItems}
            results={results}
            groupedResults={groupedResults}
            highlightedIndex={effectiveHighlightIndex}
            query={query}
            onSelectResult={onSelectResult}
            onSelectAction={onSelectAction}
            onClearFilters={onClearFilters}
            onSearchAll={onSearchAll}
            onRetry={onRetry}
            activeFilters={activeFilters}
            labels={labels}
          />
          <div className={['h-px shrink-0', GLOBAL_SEARCH_SEPARATOR_CLASS].join(' ')} role="separator" />
          <TopbarGlobalSearchFooter labels={labels} onAdvancedSearch={onAdvancedSearch} />
        </div>
      </div>
    </>
  );

  const modalContent =
    useBodyPortal && typeof document !== 'undefined'
      ? createPortal(modalTree, document.body)
      : modalTree;

  return (
    <>
      {shortcutListener}
      {modalContent}
    </>
  );
};

export default TopbarGlobalSearchModal;
